import * as React from 'react'
import { Icon, Spinner } from '@blueprintjs/core'
import classNames from 'classnames'

type Variant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'success' | 'warning'
type Size = 'default' | 'sm' | 'lg'

type Intent = 'none' | 'primary' | 'success' | 'warning' | 'danger' | 'light'

type IconInput = React.ReactNode | string

interface SharedProps {
    variant?: Variant
    size?: Size
    text?: React.ReactNode
    icon?: IconInput
    rightIcon?: IconInput
    minimal?: boolean
    outlined?: boolean
    small?: boolean
    large?: boolean
    loading?: boolean
    fill?: boolean
    intent?: Intent
    alignText?: 'left' | 'center' | 'right'
}

export interface ButtonProps extends SharedProps, React.ButtonHTMLAttributes<HTMLButtonElement> {}

export interface AnchorButtonProps
    extends SharedProps,
        React.AnchorHTMLAttributes<HTMLAnchorElement> {
    disabled?: boolean
}

const resolveVariant = ({
    variant,
    minimal,
    outlined,
    intent,
}: Pick<SharedProps, 'variant' | 'minimal' | 'outlined' | 'intent'>) => {
    if (variant) {
        return variant
    }
    if (outlined) {
        return 'outline'
    }
    if (minimal) {
        return 'ghost'
    }
    if (intent === 'success') {
        return 'success'
    }
    if (intent === 'warning') {
        return 'warning'
    }
    if (intent === 'danger') {
        return 'destructive'
    }
    if (intent === 'primary') {
        return 'default'
    }
    if (intent === 'none' || intent === 'light') {
        return 'secondary'
    }
    return 'outline'
}

const resolveSize = ({ size, small, large }: Pick<SharedProps, 'size' | 'small' | 'large'>) => {
    if (size) {
        return size
    }
    if (small) {
        return 'sm'
    }
    if (large) {
        return 'lg'
    }
    return 'default'
}

const buttonClasses = ({
    className,
    variant,
    size,
    alignText = 'center',
    fill,
}: {
    className?: string
    variant: Variant
    size: Size
    alignText?: SharedProps['alignText']
    fill?: boolean
}) =>
    classNames(
        'inline-flex items-center whitespace-nowrap rounded-md font-normal transform-gpu transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mjBlue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:transform-none disabled:shadow-none',
        {
            relative: true,
            'shadow-sm hover:shadow-md active:shadow-sm': variant !== 'ghost',
            'bg-[#2d72d2] text-white hover:bg-[#215db0] active:bg-[#184a90]': variant === 'default',
            'bg-[#cd4246] text-white hover:bg-[#ac2f33] active:bg-[#8e292c]':
                variant === 'destructive',
            'bg-[#238551] text-white hover:bg-[#1c6e42] active:bg-[#165a36]': variant === 'success',
            'bg-[#c87619] text-white hover:bg-[#935610] active:bg-[#77450d]': variant === 'warning',
            'border border-[rgba(28,33,39,0.2)] bg-white text-[#1c2127] hover:bg-[#f6f7f9] active:bg-[#edeff2]':
                variant === 'outline',
            'bg-[#f6f7f9] text-[#1c2127] hover:bg-[#edeff2] active:bg-[#dce0e5]':
                variant === 'secondary',
            'bg-transparent text-[#1c2127] hover:bg-[rgba(143,153,168,0.15)] active:bg-[rgba(143,153,168,0.3)]':
                variant === 'ghost',
            'min-h-[30px] min-w-[30px] px-[10px] py-[5px] text-sm': size === 'default',
            'min-h-[24px] min-w-[24px] px-[7px] py-0 text-sm': size === 'sm',
            'min-h-[40px] min-w-[40px] px-[15px] py-[5px] text-base': size === 'lg',
            'justify-start': alignText === 'left',
            'justify-center': alignText === 'center',
            'justify-end': alignText === 'right',
            'w-full': fill,
        },
        className,
    )

const renderIcon = (icon: IconInput, className?: string) => {
    if (icon == null) {
        return null
    }
    if (React.isValidElement(icon)) {
        return <span className={className}>{icon}</span>
    }
    return <Icon icon={icon as any} className={className} />
}

function ButtonContent({
    loading,
    icon,
    rightIcon,
    size,
    text,
    variant,
    children,
}: Pick<SharedProps, 'loading' | 'icon' | 'rightIcon' | 'size' | 'text'> & {
    variant: Variant
    children?: React.ReactNode
}) {
    const content = text ?? children
    const hasContent = content != null
    const iconGap = size === 'lg' ? 'mr-[10px]' : 'mr-[7px]'
    const rightIconGap = size === 'lg' ? 'ml-[10px]' : 'ml-[7px]'
    const solidSpinner = ['default', 'destructive', 'success', 'warning'].includes(variant)

    return (
        <>
            {loading && (
                <Spinner
                    size={14}
                    className={classNames(
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                        solidSpinner && 'spinner-white',
                    )}
                />
            )}
            <span className={classNames('inline-flex items-center', loading && 'invisible')}>
                {renderIcon(icon, hasContent ? iconGap : undefined)}
                {content}
                {renderIcon(rightIcon, hasContent ? rightIconGap : undefined)}
            </span>
        </>
    )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            minimal,
            outlined,
            small,
            large,
            loading = false,
            disabled = false,
            fill,
            intent,
            alignText = 'center',
            icon,
            rightIcon,
            text,
            children,
            type = 'button',
            ...props
        },
        ref,
    ) => {
        const resolvedVariant = resolveVariant({ variant, minimal, outlined, intent })
        const resolvedSize = resolveSize({ size, small, large })
        return (
            <button
                className={buttonClasses({
                    className,
                    variant: resolvedVariant,
                    size: resolvedSize,
                    alignText,
                    fill,
                })}
                ref={ref}
                type={type}
                disabled={loading || disabled}
                {...props}
            >
                <ButtonContent
                    loading={loading}
                    icon={icon}
                    rightIcon={rightIcon}
                    size={resolvedSize}
                    text={text}
                    variant={resolvedVariant}
                >
                    {children}
                </ButtonContent>
            </button>
        )
    },
)
Button.displayName = 'Button'

const AnchorButton = React.forwardRef<HTMLAnchorElement, AnchorButtonProps>(
    (
        {
            className,
            variant,
            size,
            minimal,
            outlined,
            small,
            large,
            loading = false,
            disabled = false,
            fill,
            intent,
            alignText = 'center',
            icon,
            rightIcon,
            text,
            children,
            href,
            onClick,
            ...props
        },
        ref,
    ) => {
        const resolvedVariant = resolveVariant({ variant, minimal, outlined, intent })
        const resolvedSize = resolveSize({ size, small, large })
        const isDisabled = loading || disabled

        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (isDisabled) {
                event.preventDefault()
                event.stopPropagation()
                return
            }

            onClick?.(event)
        }

        return (
            <a
                className={buttonClasses({
                    className: classNames(
                        className,
                        isDisabled && 'pointer-events-none opacity-50',
                    ),
                    variant: resolvedVariant,
                    size: resolvedSize,
                    alignText,
                    fill,
                })}
                ref={ref}
                {...props}
                href={isDisabled ? undefined : href}
                aria-disabled={isDisabled || undefined}
                tabIndex={isDisabled ? -1 : props.tabIndex}
                onClick={handleClick}
            >
                <ButtonContent
                    loading={loading}
                    icon={icon}
                    rightIcon={rightIcon}
                    size={resolvedSize}
                    text={text}
                    variant={resolvedVariant}
                >
                    {children}
                </ButtonContent>
            </a>
        )
    },
)
AnchorButton.displayName = 'AnchorButton'

export { AnchorButton, Button }
